import { Chip as MuiChip, Box } from '@mui/material'

const palette = {
  red: { bg: 'rgba(255,61,87,0.15)', fg: '#ff3d57', border: '#ff3d57' },
  blue: { bg: 'rgba(59,130,246,0.16)', fg: '#3B82F6', border: '#3B82F6' },
  green: { bg: 'rgba(31,216,168,0.15)', fg: '#1fd8a8', border: '#1fd8a8' },
  orange: { bg: 'rgba(255,166,92,0.15)', fg: '#ffa65c', border: '#ffa65c' },
  yellow: { bg: 'rgba(255,184,75,0.15)', fg: '#f7b84b', border: '#f7b84b' },
  purple: { bg: 'rgba(125,92,255,0.15)', fg: '#7d5cff', border: '#7d5cff' },
  gray: {
    bg: 'var(--chip-filled-bg)',
    fg: 'var(--filter-chip-text)',
    border: 'var(--chip-filled-border)',
  },
  default: {
    bg: 'var(--chip-filled-bg)',
    fg: 'var(--filter-chip-text)',
    border: 'var(--chip-filled-border)',
  },
}

function StatusBadge({
  children,
  value,
  config = {},
  fallback,
  color,
  dot = false,
  dotColor,
  dotSize = 6,
  icon,
  shape = 'chip',
  size = 'small',
  variant = 'filled',
  hasBorder = false,
  sx = {},
  ...rest
}) {
  const fromConfig =
    value !== undefined ? config[value] || fallback || Object.values(config)[0] || {} : null

  let bgColor = 'var(--chip-filled-bg)'
  let fgColor = 'var(--filter-chip-text)'
  let borderColor = 'var(--chip-filled-border)'
  let label
  let resolvedDotColor

  if (color) {
    const c = palette[color] || palette.default
    bgColor = c.bg
    fgColor = c.fg
    borderColor = c.border
    label = children ?? value
  } else if (fromConfig) {
    if (typeof fromConfig === 'string') {
      fgColor = fromConfig
      resolvedDotColor = fromConfig
      label = children ?? value
    } else {
      const { color: cfgColor, bgcolor: cfgBg, border: cfgBorder, dot: cfgDot } = fromConfig
      bgColor = cfgBg || bgColor
      fgColor = cfgColor || fgColor
      borderColor = cfgBorder || borderColor
      label = children ?? value
      resolvedDotColor = cfgDot
    }
  } else {
    label = children ?? value
  }

  resolvedDotColor = dotColor || resolvedDotColor || fgColor
  const showDot = Boolean(dot)

  const dotNode = showDot ? (
    <Box
      component="span"
      sx={{
        width: dotSize,
        height: dotSize,
        borderRadius: '50%',
        backgroundColor: resolvedDotColor,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  ) : null

  // Optional leading icon (e.g. spinning loader for "processing" rows).
  // Replaces the static dot when present; keeps dot spacing consistent
  // so callers can swap dot → icon without redesigning the badge.
  const iconNode = icon ? (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: resolvedDotColor,
        flexShrink: 0,
        '@keyframes status-badge-spin': {
          to: { transform: 'rotate(360deg)' },
        },
        animation: 'status-badge-spin 1s linear infinite',
      }}
    >
      {icon}
    </Box>
  ) : null

  if (variant === 'plain') {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: iconNode || showDot ? 0.75 : 0,
          fontSize: '14px',
          fontWeight: 400,
          color: 'var(--table-text)',
          backgroundColor: 'transparent',
          border: 'none',
          padding: 0,
          height: 'auto',
          ...sx,
        }}
      >
        {iconNode || dotNode}
        <Box component="span">{label}</Box>
      </Box>
    )
  }

  const labelNode = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: iconNode ? 6 : showDot ? 6 : 0,
      }}
    >
      {iconNode || dotNode}
      <span>{label}</span>
    </span>
  )

  if (shape === 'pill') {
    return (
      <MuiChip
        label={labelNode}
        size={size}
        sx={{
          bgcolor: bgColor,
          color: fgColor,
          border: hasBorder ? `1px solid ${borderColor}` : 'none',
          fontWeight: 600,
          fontSize: '12px',
          height: 24,
          borderRadius: 10,
          '& .MuiChip-label': { px: 1 },
          ...sx,
        }}
        {...rest}
      />
    )
  }

  return (
    <MuiChip
      label={labelNode}
      size={size}
      sx={{
        bgcolor: bgColor,
        color: fgColor,
        border: hasBorder ? `1px solid ${borderColor}` : 'none',
        fontWeight: 600,
        fontSize: '12px',
        height: 24,
        borderRadius: 1,
        '& .MuiChip-label': { px: 1.25 },
        ...sx,
      }}
      {...rest}
    />
  )
}

export default StatusBadge
